import React from 'react';
import {
    createTable,
    RenderingPresetType,
    TableColumnSpec,
    TableHeaderSpec,
} from '../../../../../../../components/table/CreateTable';
import type { FixedAsset } from './FixedAssetsTab';
import { formatDate } from '../../../../../../../lib/utils.ts';
import { LinkButton } from '../../../../../../../components/general/LinkButton.tsx';

interface FixedAssetTableProps {
    items: FixedAsset[];
    isLoading: boolean;
    onOpenBill?: (documentId: string) => void;
}

export function linkedBillLabel(linkedBill: FixedAsset['linked_bill']): string {
    if (!linkedBill) {
        return '—';
    }
    return [
        linkedBill.relation_name,
        linkedBill.invoice_number,
        linkedBill.invoice_date ? formatDate(linkedBill.invoice_date) : null,
    ].filter(Boolean).join(' · ') || 'Factuur';
}

export default function FixedAssetTable({ items, isLoading, onOpenBill }: FixedAssetTableProps) {
    const columns: TableColumnSpec<FixedAsset>[] = [
        {
            name: 'Naam',
            field: 'name',
        },
        {
            name: 'Aanschafdatum',
            field: 'purchase_date',
            renderingPreset: RenderingPresetType.Date,
        },
        {
            name: 'Aanschafwaarde',
            field: 'purchase_value_cents',
            renderingPreset: RenderingPresetType.Money,
        },
        {
            name: 'Restwaarde',
            field: 'residual_value_cents',
            renderingPreset: RenderingPresetType.Money,
        },
        {
            name: 'Levensduur (maanden)',
            field: 'economic_lifetime_months',
        },
        {
            name: 'Rekening activa',
            field: 'balance_sheet_gl_account_label',
        },
        {
            name: 'Rekening afschrijvingen',
            field: 'profit_and_loss_gl_account_label',
        },
        ...(onOpenBill ? [{
            name: 'Factuur',
            field: '__linked_bill__',
            disableSorting: true,
            customRender: (fixedAsset: FixedAsset) => {
                const linkedBill = fixedAsset.linked_bill;
                if (!linkedBill) {
                    return linkedBillLabel(null);
                }
                return (
                    <LinkButton
                        stopPropagation
                        testId="linked-bill-link"
                        onClick={() => onOpenBill(linkedBill.document_id)}
                    >
                        {linkedBillLabel(linkedBill)}
                    </LinkButton>
                );
            },
        }] : []),
    ];

    const tableSpec: TableHeaderSpec<FixedAsset> = {
        columns,
        onEmptyRender: () => (
            <div className="text-center py-8 text-gray-500 bg-white rounded-lg border border-gray-200">
                Geen vaste activa gevonden voor deze klant.
            </div>
        ),
    };

    const TableComponent = createTable(items, tableSpec);

    if (isLoading) {
        return (
            <div className="mt-4 text-center py-8 text-gray-500 bg-white rounded-lg border border-gray-200">
                Laden…
            </div>
        );
    }

    return (
        <div className="mt-4">
            <TableComponent />
        </div>
    );
}
